import React, { useState, useEffect, useMemo, useRef } from "react";
//gui elements
import {
  Button,
  TextField,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Typography,
  Tooltip,
} from "@mui/material";
//icons
import {
  Logout as LogoutIcon,
  Palette as PaletteIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  SettingsBrightness as SettingsBrightnessIcon,
  ChevronRight as ChevronRightIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Menu as MenuIcon,
  EmojiEvents as EmojiEventsIcon,
  Star as StarIcon,
  Schedule as ScheduleIcon,
  Grading as GradingIcon,
  MenuBook as MenuBookIcon,
} from "@mui/icons-material";

//Custom components
import DataReader from "./components/DataReader";
import DataSubmitter from "./components/DataSubmitter";
import CurrentCategorySidebar from "./components/CurrentCategorySidebar";
import TopScoresSidebar from "./components/TopScoresSidebar";
import SpecialAwardsSidebar from "./components/SpecialAwardsSidebar";
import AwardsRail from "./components/AwardsRail";
import SubmittedAwardsDialog from "./components/SubmittedAwardsDialog";
import JudgesChoiceDialog from "./components/JudgesChoiceDialog";
import RulesPanel from './components/RulesPanel';
import SchedulePanel from './components/SchedulePanel';

import { API_BASE } from '../config';
import { applyCssVars } from '../theme';
import usePageTitle from '../shared/hooks/usePageTitle';
import "../index.css";
import {
  useFetchEntries,
  useUser,
  useJudgeResponses,
  useJudgesChoice,
  useSpecialAwards,
} from './hooks/fetchData';


function JudgeApp({ mode = "light", setMode }) {
  usePageTitle('OSSDF Judging');

  // References ----------------------------------------------------------------------------------------------------------
  const debounceTimer = useRef(null);
  const debounceDelay = 1500;

  // State and Data Fetching via Hooks----------------------------------------------------------------------------------
  const { scoreData, loading, error, sessionExpired, setSessionExpired, loadEntries } = useFetchEntries(API_BASE);
  const { user } = useUser(API_BASE, setSessionExpired);
  const { judgeResponses, setJudgeResponses } = useJudgeResponses(API_BASE, user, setSessionExpired);

  // On first visit (no prior login), redirect to login page
  useEffect(() => {
    if (sessionExpired && !localStorage.getItem('ossdf_hasLoggedIn')) {
      window.location.href = `${process.env.PUBLIC_URL}/`;
    }
  }, [sessionExpired]);

  //Column indexes for scoreData -----------------------------------------------
  const getColIndex = (row, colName) => row.findIndex(h => String(h).trim().toLowerCase().includes(colName.toLowerCase()));
  const colIndexes = useMemo(() => {
    if (!scoreData || scoreData.length === 0) {
      return {
        entryIdx: 0, titleIdx: 1, levelIdx: 3, styleIdx: 4, sizeIdx: 5
      };
    }

    const scoreHeaderRow = scoreData[0] || [];

    return {
      entryIdx: getColIndex(scoreHeaderRow, "entry"),
      titleIdx: getColIndex(scoreHeaderRow, "title"),
      choreoIdx: getColIndex(scoreHeaderRow, "choreographer"),
      newChoreoIdx: getColIndex(scoreHeaderRow, "new choreographer"),
      dancerIdx: getColIndex(scoreHeaderRow, "dancer(s)"),
      levelIdx: getColIndex(scoreHeaderRow, "level"),
      styleIdx: getColIndex(scoreHeaderRow, "style"),
      sizeIdx: getColIndex(scoreHeaderRow, "size"),
      numDancersIdx: getColIndex(scoreHeaderRow, "number of dancers"),
      schoolIdx: getColIndex(scoreHeaderRow, "school code"),
      lockedIdx: getColIndex(scoreHeaderRow, "locked"),
    };
  }, [scoreData]);

  const judgesColIndexes = useMemo(() => {
    if (!judgeResponses || judgeResponses.length === 0) {
      return {
        entryIdx: 1, titleIdx: 2, levelIdx: 3, styleIdx: 4, sizeIdx: 5, schoolIdx: 6, artisticIdx: 7, technicalIdx: 8, notesIdx: 9, nomIdx: 10, favIdx: 11
      };
    }

    const header = judgeResponses && judgeResponses.length > 0 ? judgeResponses[0] : [];
    return {
      entryIdx: getColIndex(header, "entry"),
      titleIdx: getColIndex(header, "title"),
      levelIdx: getColIndex(header, "level"),
      styleIdx: getColIndex(header, "style"),
      sizeIdx: getColIndex(header, "size"),
      schoolIdx: getColIndex(header, "school"),
      artisticIdx: getColIndex(header, "artistic"),
      technicalIdx: getColIndex(header, "technical"),
      notesIdx: getColIndex(header, "notes"),
      nomIdx: getColIndex(header, "nominated"),
      favIdx: getColIndex(header, "favourite"),
    };
  }, [judgeResponses]);

  // Row index state ----------------------------------------------------------------------------------------------------------
  const getUserRowIndexKey = (userEmail) => userEmail ? `rowIndex_${userEmail}` : "rowIndex";

  const [rowIndex, setRowIndex] = useState(() => {
    const saved = localStorage.getItem("rowIndex");
    return saved ? Number(saved) : 1;
  });

  useEffect(() => {
    if (user && user.email) {
      const saved = localStorage.getItem(getUserRowIndexKey(user.email));
      setRowIndex(saved ? Number(saved) : 1);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.email) {
      localStorage.setItem(getUserRowIndexKey(user.email), rowIndex);
    }
  }, [rowIndex, user]);

  //Navigation handlers ---------------------------------------------------------------------------------------------------------
  const [jumpValue, setJumpValue] = useState("");

  const lastIndex = Math.max(1, (scoreData && scoreData.length ? scoreData.length - 1 : 1));
  const handleNext = () => {
    setRowIndex(prev => Math.min(prev + 1, lastIndex));

    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (!loading) loadEntries();
    }, debounceDelay);
  }
  const handleBack = () => {
    setRowIndex(prev => Math.max(1, prev - 1));

    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (!loading) loadEntries();
    }, debounceDelay);
  }

  const handleJump = () => {
    loadEntries();
    if (!jumpValue || isNaN(jumpValue) || jumpValue < 1) return;
    const index = scoreData.findIndex((row, i) => i > 0 && String(row[0]) === String(jumpValue));
    if (index !== -1) {
      setRowIndex(index);
    }
    setJumpValue("");
  };

  const handleSelectEntry = (index) => {
    setRowIndex(index);
  };

  // Hamburger menu -----------------------------------------------------------------------------------------------------------
  const [hamburgerAnchor, setHamburgerAnchor] = useState(null);
  const hamburgerMenuOpen = Boolean(hamburgerAnchor);
  const handleOpenHamburger = (event) => setHamburgerAnchor(event.currentTarget);
  const handleCloseHamburger = () => setHamburgerAnchor(null);

  const handleOpenRubric = () => {
    window.open(`${process.env.PUBLIC_URL}/rubric`, '_blank');
    handleCloseHamburger();
  };

  const judgesChoice = useJudgesChoice(API_BASE, user, judgesColIndexes, setSessionExpired);
  const specialAwards = useSpecialAwards(API_BASE, user, setSessionExpired);

  // Account Menu and theme states ---------------------------------------------------------------------------------------
  const [accountAnchor, setAccountAnchor] = useState(null);
  const [menuView, setMenuView] = useState("main");

  const accountMenuOpen = Boolean(accountAnchor);

  const firstInitial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();

  const handleOpenAccountMenu = (event) => {
    setAccountAnchor(event.currentTarget);
    setMenuView("main");
  }
  const handleCloseMenus = () => {
    setAccountAnchor(null);
    setMenuView("main");
  };

  const applyThemeChoice = (choice) => {
    localStorage.setItem("themeMode", choice);

    if (typeof setMode === "function") {
      setMode(choice === "system"
        ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
        : choice);
    } else {
      const resolved = choice === "system"
        ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
        : choice;
      applyCssVars(resolved);
    }

    handleCloseMenus();
  };

  const savedThemeChoice = localStorage.getItem("themeMode") || "system";

  const handleLogout = () => {
    window.location.href = `${API_BASE}/api/logout`;
  };

  //sidebar dialog states ---------------------------------------------------------------------------------------------------
  const [awardsOpen, setAwardsOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  return (
    <>
      {/* Session expired message */}
      {sessionExpired && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          background: '#fff6f6',
          color: 'red',
          textAlign: 'center',
          fontWeight: 700,
          fontSize: '1.2rem',
          padding: '16px 0',
          zIndex: 1000,
          borderBottom: '2px solid red',
        }}>
          Your session has expired. Please <a href={`${process.env.PUBLIC_URL}/`} style={{ color: 'red', textDecoration: 'underline' }}>log in again</a>.
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 24px 0 24px",
        }}
      >

        {/* Hamburger menu — left side */}
        <IconButton onClick={handleOpenHamburger} size="large" sx={{ fontSize: '1.5rem', mr: 2 }}>
          <MenuIcon />
        </IconButton>
        <Menu
          anchorEl={hamburgerAnchor}
          open={hamburgerMenuOpen}
          onClose={handleCloseHamburger}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <MenuItem onClick={() => {
            handleCloseHamburger();
            specialAwards.handleOpen();
          }}>
            <ListItemIcon><EmojiEventsIcon fontSize="small" /></ListItemIcon>
            <ListItemText>View All Special Awards</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            handleCloseHamburger();
            judgesChoice.handleOpen();
          }}>
            <ListItemIcon><StarIcon fontSize="small" /></ListItemIcon>
            <ListItemText>View My Judges Choice Nominations</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            handleCloseHamburger();
            setRulesOpen(true);
          }}>
            <ListItemIcon><MenuBookIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Rules &amp; Regulations</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            handleCloseHamburger();
            setScheduleOpen(true);
          }}>
            <ListItemIcon><ScheduleIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Schedule</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            handleOpenRubric();
          }}>
            <ListItemIcon><GradingIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Rubrics</ListItemText>
          </MenuItem>
        </Menu>

        {/* App title in the center */}
        <Typography variant="title" sx={{ margin: 0 }}>OSSDF Scoring</Typography>

        {/* Awards Shortcuts */}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
          <Tooltip title="View all special awards" enterDelay={400} leaveDelay={0}>
            <IconButton onClick={specialAwards.handleOpen} sx={{
              '&:hover': {
                backgroundColor: 'transparent',
                color: 'primary.main',
              }
            }}>
              <EmojiEventsIcon color="inherit" fontSize="medium" sx={{ mr: 2 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="View my Judges Choice nominations">
            <IconButton onClick={judgesChoice.handleOpen} sx={{
              '&:hover': {
                backgroundColor: 'transparent',
                color: 'primary.main',
              }
            }}>
              <StarIcon color="inherit" fontSize="medium" sx={{ mr: 4 }} />
            </IconButton>
          </Tooltip>

          {/* User avatar and menu — right side */}
          {user && (
            <div>
              <IconButton onClick={handleOpenAccountMenu} size="small">
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontWeight: 700
                  }}
                >
                  {firstInitial}
                </Avatar>
              </IconButton>

              <Menu
                anchorEl={accountAnchor}
                open={accountMenuOpen}
                onClose={handleCloseMenus}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                {menuView === "main" ? (
                  <div>
                    <MenuItem onClick={() => setMenuView("theme")}>
                      <ListItemIcon>
                        <PaletteIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Theme</ListItemText>
                      <ListItemIcon sx={{ minWidth: 24, justifyContent: "flex-end" }}>
                        <ChevronRightIcon fontSize="small" />
                      </ListItemIcon>
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                      <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Logout</ListItemText>
                    </MenuItem>
                  </div>
                ) : (
                  <div>
                    <MenuItem onClick={() => setMenuView("main")}>
                      <ListItemIcon> <ArrowBackIcon fontSize="small" /> </ListItemIcon>
                      <ListItemText>Theme</ListItemText>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => applyThemeChoice("light")}>
                      <ListItemIcon> <LightModeIcon fontSize="small" /> </ListItemIcon>
                      <ListItemText>Light Mode</ListItemText>
                      {savedThemeChoice === "light" && <CheckIcon fontSize="small" />}
                    </MenuItem>
                    <MenuItem onClick={() => applyThemeChoice("dark")}>
                      <ListItemIcon> <DarkModeIcon fontSize="small" /> </ListItemIcon>
                      <ListItemText>Dark Mode</ListItemText>
                      {savedThemeChoice === "dark" && <CheckIcon fontSize="small" />}
                    </MenuItem>
                    <MenuItem onClick={() => applyThemeChoice("system")}>
                      <ListItemIcon> <SettingsBrightnessIcon fontSize="small" /> </ListItemIcon>
                      <ListItemText>Use System Settings</ListItemText>
                      {savedThemeChoice === "system" && <CheckIcon fontSize="small" />}
                    </MenuItem>

                  </div>
                )}
              </Menu>
            </div>
          )}
        </div>
      </div>
      {error ? (
        <div style={{ color: "red", textAlign: "center", marginTop: 20 }}>{error}</div>
      ) : (
        <div className='app-main'>
          {/* Sidebar on the left */}
          <div className='app-sidepane-stack'>

            {/* Top Scores Sidebar to the right of the main box */}
            <div className='app-sidebar app-sidebar--topscores'>
              <TopScoresSidebar
                scoreData={scoreData}
                rowIndex={rowIndex}
                judgeResponses={judgeResponses}
                colIndexes={{
                  titleIdx: colIndexes.titleIdx,
                  choreoIdx: colIndexes.choreoIdx,
                  levelIdx: colIndexes.levelIdx,
                  sizeIdx: colIndexes.sizeIdx,
                }}
                judgeColIndexes={{
                  entryIdx: judgesColIndexes.entryIdx,
                  artisticIdx: judgesColIndexes.artisticIdx,
                  technicalIdx: judgesColIndexes.technicalIdx,
                  notesIdx: judgesColIndexes.notesIdx,
                }}
              />
            </div>

            {/* Current Category Sidebar to the left of the main box */}
            <div className='app-sidebar app-sidebar--currentcategory'>
              <CurrentCategorySidebar
                rowIndex={rowIndex}
                scoreData={scoreData}
                user={user}
                judgeResponses={judgeResponses}
                onSelectEntry={handleSelectEntry}
                colIndexes={{
                  entryNumIdx: colIndexes.entryIdx,
                  titleIdx: colIndexes.titleIdx,
                  levelIdx: colIndexes.levelIdx,
                  styleIdx: colIndexes.styleIdx,
                  sizeIdx: colIndexes.sizeIdx,
                  choreoIdx: colIndexes.choreoIdx,
                }}
                judgesColIndexes={{
                  artisticIdx: judgesColIndexes.artisticIdx,
                  technicalIdx: judgesColIndexes.technicalIdx,
                }}
              />
            </div>
          </div>

          {/* Main content box */}
          <div className='app-center'>
            <div className='app-content-box'>
              {/* Entry Navigation */}
              <div className='app-entry-navigation'>
                {/* Back button for previous entry */}
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={handleBack}
                  disabled={rowIndex <= 1}
                >
                  Prev
                </Button>
                {/* Jump-to-entry number field (no spinner arrows) */}
                <div style={{ display: 'flex', marginLeft: 'auto', alignItems: 'flex-end', gap: 8 }}>
                  <TextField
                    label="Go to Entry"
                    type="text"
                    size="small"
                    variant="outlined"
                    autoComplete="off"
                    value={jumpValue}
                    onChange={e => setJumpValue(e.target.value.replace(/[^0-9]/g, ''))}
                    style={{ width: 110 }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    sx={{
                      '& input': {
                        appearance: 'textfield',
                        MozAppearance: 'textfield',
                        WebkitAppearance: 'none',
                      }
                    }}
                  />
                  {/* Go button for jump-to-entry */}
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={handleJump}
                    disabled={!jumpValue || isNaN(jumpValue) || jumpValue < 1 || scoreData.findIndex((row, i) => i > 0 && String(row[0]) === String(jumpValue)) === -1}
                  >
                    Go
                  </Button>

                  {/* Next button for next entry */}
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleNext}
                    disabled={rowIndex >= scoreData.length - 1}
                  >
                    Next
                  </Button>
                </div>
              </div>
              <div className='app-data-reader' >
                <DataReader
                  rowIndex={rowIndex}
                  scoreData={scoreData}
                  colIndexes={{
                    entryNumIdx: colIndexes.entryIdx,
                    titleIdx: colIndexes.titleIdx,
                    choreoIdx: colIndexes.choreoIdx,
                    newChoreoIdx: colIndexes.newChoreoIdx,
                    dancerIdx: colIndexes.dancerIdx,
                    styleIdx: colIndexes.styleIdx,
                    levelIdx: colIndexes.levelIdx,
                    sizeIdx: colIndexes.sizeIdx,
                    numDancersIdx: colIndexes.numDancersIdx,
                    schoolIdx: colIndexes.schoolIdx
                  }}
                />
              </div>
              <div className='app-data-submitter'>
                <DataSubmitter
                  user={user}
                  rowIndex={rowIndex}
                  scoreData={scoreData}
                  onNext={handleNext}
                  judgeResponses={judgeResponses}
                  setJudgeResponses={setJudgeResponses}
                  colIndexes={{
                    titleIdx: colIndexes.titleIdx,
                    levelIdx: colIndexes.levelIdx,
                    styleIdx: colIndexes.styleIdx,
                    sizeIdx: colIndexes.sizeIdx,
                    schoolIdx: colIndexes.schoolIdx,
                    lockedIdx: colIndexes.lockedIdx,
                  }}
                  judgesColIndexes={{
                    artisticIdx: judgesColIndexes.artisticIdx,
                    technicalIdx: judgesColIndexes.technicalIdx,
                    notesIdx: judgesColIndexes.notesIdx,
                    nomIdx: judgesColIndexes.nomIdx,
                    favIdx: judgesColIndexes.favIdx,
                  }}
                />
              </div>
            </div>
          </div>


          {/* Special Awards drawer — shown when awardsOpen is true */}
          {awardsOpen && (
            <div className='app-sidebar'>
              <SpecialAwardsSidebar
                scoreData={scoreData}
                rowIndex={rowIndex}
                colIndexes={{
                  entryIdx: colIndexes.entryIdx,
                  titleIdx: colIndexes.titleIdx,
                  levelIdx: colIndexes.levelIdx,
                  styleIdx: colIndexes.styleIdx,
                  sizeIdx: colIndexes.sizeIdx,
                  schoolIdx: colIndexes.schoolIdx,
                }}
                user={user}
                API_BASE={API_BASE}
                onClose={() => setAwardsOpen(false)}
              />
            </div>
          )}

          {/* Awards rail tab — fixed to right edge, shown when drawer is closed */}
          {!awardsOpen && (
            <AwardsRail onOpen={() => setAwardsOpen(true)} />
          )}


        </div>
      )}
      <JudgesChoiceDialog
        open={judgesChoice.open}
        onClose={judgesChoice.handleClose}
        loading={judgesChoice.loading}
        judgeChoices={judgesChoice.data}
        judgesColIndexes={{
          entryIdx: judgesColIndexes.entryIdx,
          titleIdx: judgesColIndexes.titleIdx,
          levelIdx: judgesColIndexes.levelIdx,
          styleIdx: judgesColIndexes.styleIdx,
          sizeIdx: judgesColIndexes.sizeIdx,
          schoolIdx: judgesColIndexes.schoolIdx,
          artisticIdx: judgesColIndexes.artisticIdx,
          technicalIdx: judgesColIndexes.technicalIdx,
          notesIdx: judgesColIndexes.notesIdx,
          nomIdx: judgesColIndexes.nomIdx,
          favIdx: judgesColIndexes.favIdx,
        }}
      />
      <SubmittedAwardsDialog
        open={specialAwards.open}
        onClose={specialAwards.handleClose}
        loading={specialAwards.loading}
        awards={specialAwards.data}
        schools={specialAwards.schools}
      />
      <RulesPanel open={rulesOpen} onClose={() => setRulesOpen(false)} />
      <SchedulePanel open={scheduleOpen} onClose={() => setScheduleOpen(false)} />
    </>
  );
}

export default JudgeApp;
